import { useState, useEffect, FormEvent } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import AssignmentIcon from "@mui/icons-material/Assignment";
import {
  Container,
  Dialog,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";

import conf from "config-dlm";
import { useAuth } from "components/auth/AuthProvider";
import SectionHeader from "components/utils/SectionHeader";
import {
  ProjectConfigSchema,
  usePavmApplianceEditConfig,
} from "hooks/pavm/base";

type HistoryUnit = 60000 | 3600000;

// helper method to determine default history interval unit (minutes vs. hours)
const deduceUnit = (interval: number) => (interval > 3600000 ? 3600000 : 60000);

const UnitSelect = (props: {
  id: string;
  disabled: boolean;
  value: HistoryUnit;
  onChange: (e: any) => void;
}) => (
  <FormControl fullWidth>
    <InputLabel id={props.id}>Unit</InputLabel>

    <Select
      labelId={props.id}
      id={props.id}
      label="Unit"
      value={props.value}
      disabled={props.disabled}
      onChange={props.onChange}
    >
      <MenuItem value={60000}>minutes</MenuItem>
      <MenuItem value={3600000}>hours</MenuItem>
    </Select>
  </FormControl>
);

export const EditView = (props: {
  type: string;
  title: string;
  open: boolean;
  version: string;
  config: ProjectConfigSchema;
  onClose: () => void;
}) => {
  const { onClose, open, config, version } = props;
  const { history } = config;

  const { user } = useAuth();
  const uri = user.project.uri;

  const [locked, setLocked] = useState(true);

  // general
  const [ip, setIp] = useState(config.ip);
  const [host, setHost] = useState(config.host);
  const [location, setLocation] = useState(config.location);
  const [scommBaseUri, setScommBaseUri] = useState(config.scommBaseUri);
  const [sstatBaseUri, setSstatBaseUri] = useState(config.sstatBaseUri);

  // history collection
  const defaultHostUnit = deduceUnit(history.interval);
  const [collectHistory, setCollectHistory] = useState(history.on);
  const [hostUnit, setHostUnit] = useState<HistoryUnit>(defaultHostUnit);
  const [hostInterval, setHostInterval] = useState(history.interval / hostUnit);

  const { mutate: update, isSuccess } = usePavmApplianceEditConfig();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    const hostSum = hostInterval * hostUnit;

    const body = {
      host,
      ip,
      location,
      scommBaseUri,
      sstatBaseUri,
      history: {
        on: collectHistory,
        interval: hostSum,
      },
    };

    // send config update
    update({ uri, config: body });
  };

  const resetForm = (config: ProjectConfigSchema) => {
    setHost(config.host);
    setIp(config.ip);
    setLocation(config.location);
    setScommBaseUri(config.scommBaseUri);
    setSstatBaseUri(config.sstatBaseUri);

    const history = config.history;
    const defaultHostUnit = deduceUnit(history.interval);

    setCollectHistory(history.on);
    setHostUnit(defaultHostUnit);
    setHostInterval(history.interval / defaultHostUnit);
  };

  useEffect(() => {
    if (isSuccess) setLocked(false);
  }, [isSuccess]);

  return (
    <Dialog maxWidth="md" open={open} onClose={onClose}>
      <Container component="main" sx={conf.STYLE.DIALOG}>
        <SectionHeader
          key="edit-config"
          title="Edit config"
          icon={<AssignmentIcon />}
          buttons={[
            <Button
              key={"edit-config-reset"}
              variant="outlined"
              onClick={() => resetForm(config)}
            >
              Reset
            </Button>,
          ]}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <Box component="form" noValidate onSubmit={handleSubmit}>
            <Grid item xs={12}>
              <Typography>
                <b>Main</b>
              </Typography>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="host"
                  name="host"
                  label="Host"
                  autoComplete="host-name"
                  value={host}
                  disabled
                  onChange={(e: any) => setHost(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  disabled
                  fullWidth
                  id="graceVersion"
                  name="graceVersion"
                  label="GRACE Version (RPM)"
                  type="grace-version"
                  autoComplete="grace-version"
                  value={version ?? "--"}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="ip"
                  name="ip"
                  label="IP Address"
                  autoComplete="ip"
                  value={ip}
                  disabled={locked}
                  onChange={(e: any) => setIp(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="location"
                  name="location"
                  label="Location"
                  autoComplete="location"
                  value={location}
                  disabled={locked}
                  onChange={(e: any) => setLocation(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="scommBaseUri"
                  name="scommBaseUri"
                  label="S-COMM URI"
                  type="scomm-base-uri"
                  autoComplete="scomm-base-uri"
                  value={scommBaseUri}
                  disabled={locked}
                  onChange={(e: any) => setScommBaseUri(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="sstatBaseUri"
                  name="sstatBaseUri"
                  label="S-STAT URI"
                  type="sstat-base-uri"
                  autoComplete="sstat-base-uri"
                  value={sstatBaseUri}
                  disabled={locked}
                  onChange={(e: any) => setSstatBaseUri(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography>
                  <b>History</b>
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      disabled={locked}
                      checked={collectHistory}
                      onClick={() => setCollectHistory(!collectHistory)}
                    />
                  }
                  label="Collect"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="intervalHost"
                  name="intervalHost"
                  label="Interval"
                  type="interval"
                  autoComplete="interval-host"
                  value={hostInterval}
                  disabled={locked}
                  onChange={(e: any) => setHostInterval(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <UnitSelect
                  id="host-unit-select"
                  disabled={locked}
                  value={hostUnit}
                  onChange={(e: any) => setHostUnit(e.target.value)}
                />
              </Grid>
            </Grid>

            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
            >
              <Button
                variant={locked ? "contained" : "outlined"}
                onClick={() => setLocked(!locked)}
                sx={{ mt: 3, mb: 2, mr: 2 }}
              >
                {locked ? "Edit" : "Cancel"}
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={locked}
                sx={{ mt: 3, mb: 2 }}
              >
                Save
              </Button>
            </Stack>
          </Box>
        </Box>
      </Container>
    </Dialog>
  );
};
